import React, { useEffect, useRef, useState } from 'react';
import { DayPicker, DateRange as RDPRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useTheme } from '../../../../utils/theme-context';
import { nowRome } from '../../../../utils/date';

type DateRange = [Date, Date] | null;

interface Props {
  label: string;
  labelStyle?: string;
  containerStyle?: string;
  uniqueName?: string;
  value: DateRange;
  onChange: (range: DateRange, name?: string) => void;
  showTime?: boolean;
  disabled?: boolean;
}

const formatDate = (date: Date): string =>
  `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

const DateRangePopover = ({
  label,
  labelStyle,
  containerStyle,
  value,
  uniqueName,
  onChange,
  showTime = false,
  disabled = false,
}: Props) => {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [range, setRange] = useState<RDPRange | undefined>(() =>
    value ? { from: value[0], to: value[1] } : undefined
  );

  const [fromTime, setFromTime] = useState<string>('00:00');
  const [toTime, setToTime] = useState<string>('23:59');

  useEffect(() => {
    if (disabled) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (disabled) return;
    if (range?.from && range?.to) {
      if (showTime) {
        const [fromHour, fromMinute] = fromTime.split(':').map(Number);
        const [toHour, toMinute] = toTime.split(':').map(Number);

        const fromWithTime = new Date(range.from);
        fromWithTime.setHours(fromHour, fromMinute, 0, 0);

        const toWithTime = new Date(range.to);
        toWithTime.setHours(toHour, toMinute, 0, 0);

        onChange([fromWithTime, toWithTime], uniqueName);
      } else {
        onChange([range.from, range.to], uniqueName);
      }
    }
  }, [range, fromTime, toTime]);

  const applyShortcut = (
    type: 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth'
  ) => {
    if (disabled) return;
    const today = nowRome();
    let from = new Date(today.getTime());
    let to = new Date(today.getTime());

    switch (type) {
      case 'yesterday':
        from.setDate(today.getDate() - 1);
        to = new Date(from.getTime());
        break;
      case 'thisWeek': {
        const day = today.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        from.setDate(today.getDate() + diffToMonday);
        to = new Date(from.getTime());
        to.setDate(from.getDate() + 6);
        break;
      }
      case 'lastWeek': {
        const day = today.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        from.setDate(today.getDate() + diffToMonday - 7);
        to = new Date(from.getTime());
        to.setDate(from.getDate() + 6);
        break;
      }
      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
    }

    setRange({ from, to });
    setOpen(false);
  };

  const displayText =
    value && value[0] && value[1]
      ? `${formatDate(value[0])}${showTime ? ` ${value[0].toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''
      } - ${formatDate(value[1])}${showTime ? ` ${value[1].toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''
      }`
      : 'mm/dd/yyyy - mm/dd/yyyy';
  const containerBase =
    'w-full flex items-center justify-between px-3 py-2 border dark:border-[#374151] rounded bg-white dark:bg-black dark:text-white';
  const containerInteractive = disabled ? ' opacity-60 cursor-not-allowed' : ' cursor-pointer';

  return (
    <div className={`relative w-full ${containerStyle}`} ref={ref}>
      <label className={`block mb-1 font-medium dark:text-white ${labelStyle}`}>{label}</label>
      <div
        aria-disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen(!open);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') setOpen(!open);
        }}
        className={`${containerBase}${containerInteractive}`}
      >
        <span className={`${value && value[0] && value[1] ? '' : 'text-primary-light'}`}>
          {displayText}
        </span>
        <img
          src={
            theme === 'dark'
              ? '/assets/insights-tab/calendar-white.svg'
              : '/assets/insights-tab/calendar.svg'
          }
          className='object-contain w-[24px]'
        />
      </div>

      {open && (
        <div className='absolute mt-1 z-50 p-4 rounded-md shadow-lg bg-white dark:bg-black dark:text-white border dark:border-gray-700 flex gap-6'>
          <div className='flex flex-col text-sm w-40'>
            {[
              { label: 'Today', key: 'today' },
              { label: 'Yesterday', key: 'yesterday' },
              { label: 'This Week', key: 'thisWeek' },
              { label: 'Last Week', key: 'lastWeek' },
              { label: 'This Month', key: 'thisMonth' },
              { label: 'Last Month', key: 'lastMonth' },
            ].map(({ label, key }) => (
              <button
                key={key}
                onClick={() => applyShortcut(key as any)}
                className='text-left px-2 py-2 border-b border-b-1 dark:border-b-[#242424] rounded hover:bg-gray-100 dark:hover:bg-gray-700'
              >
                {label}
              </button>
            ))}
          </div>

          <div className='flex flex-col gap-3'>
            <DayPicker
              mode='range'
              selected={range}
              onSelect={setRange}
              numberOfMonths={1}
              defaultMonth={range?.from}
              classNames={{
                caption: 'dark:text-white',
                head: 'dark:text-gray-300',
                cell: 'dark:hover:bg-gray-800',
              }}
              modifiersClassNames={{
                selected: 'bg-blue-500 text-white dark:bg-blue-400 dark:text-black',
                range_start: 'bg-blue-500 text-white dark:bg-blue-400 dark:text-black',
                range_end: 'bg-blue-500 text-white dark:bg-blue-400 dark:text-black',
                range_middle: 'bg-blue-100 text-black dark:bg-blue-900 dark:text-white',
              }}
            />

            {showTime && (
              <div className='flex gap-3 text-sm'>
                <div className='flex flex-col'>
                  <label className='font-medium'>Start Time</label>
                  <input
                    type='time'
                    value={fromTime}
                    onChange={(e) => setFromTime(e.target.value)}
                    className='border px-2 py-1 rounded dark:bg-black dark:text-white dark:border-gray-700'
                  />
                </div>
                <div className='flex flex-col'>
                  <label className='font-medium'>End Time</label>
                  <input
                    type='time'
                    value={toTime}
                    onChange={(e) => setToTime(e.target.value)}
                    className='border px-2 py-1 rounded dark:bg-black dark:text-white dark:border-gray-700'
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePopover;
